/**
 * Browser Notification Service
 * Handles native browser push notifications for Safari, Chrome, Edge, and Firefox
 */

export type NotificationPermissionStatus = 'default' | 'granted' | 'denied';

export interface BrowserNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  requireInteraction?: boolean;
  silent?: boolean;
  onClick?: () => void;
}

class BrowserNotificationService {
  private permissionStatus: NotificationPermissionStatus = 'default';
  private clickHandlers: Map<string, () => void> = new Map();

  constructor() {
    this.initialize();
  }

  /**
   * Initialize the service and check current permission status
   */
  private initialize(): void {
    if (!this.isSupported()) {
      console.warn('Browser notifications are not supported');
      return;
    }

    this.permissionStatus = Notification.permission;

    // Listen for notification clicks (service worker would handle this in production)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(() => {
        // Service worker notification handling would go here
      }).catch((error) => {
        console.warn('Service worker not available:', error);
      });
    }
  }

  /**
   * Check if browser notifications are supported
   */
  isSupported(): boolean {
    return 'Notification' in window;
  }

  /**
   * Get current permission status
   */
  getPermissionStatus(): NotificationPermissionStatus {
    if (!this.isSupported()) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Check if notifications are enabled (permission granted)
   */
  isEnabled(): boolean {
    return this.getPermissionStatus() === 'granted';
  }

  /**
   * Request permission to show notifications
   * Returns the permission status after user responds
   */
  async requestPermission(): Promise<NotificationPermissionStatus> {
    if (!this.isSupported()) {
      console.warn('Browser notifications are not supported');
      return 'denied';
    }

    if (this.permissionStatus === 'granted') {
      return 'granted';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permissionStatus = permission;
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  /**
   * Show a browser notification
   * Automatically requests permission if not already granted
   */
  async show(options: BrowserNotificationOptions): Promise<Notification | null> {
    if (!this.isSupported()) {
      console.warn('Browser notifications are not supported');
      return null;
    }

    // Request permission if needed
    if (this.permissionStatus !== 'granted') {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return null;
      }
    }

    try {
      const { title, body, icon, badge, tag, data, requireInteraction, silent, onClick } =
        options;

      const notificationOptions: NotificationOptions = {
        body,
        icon: icon || '/logo-192.png', // Default to app icon
        badge: badge || '/logo-192.png',
        tag: tag || `notification-${Date.now()}`,
        data,
        requireInteraction: requireInteraction || false,
        silent: silent || false,
      };

      const notification = new Notification(title, notificationOptions);

      // Store click handler if provided
      if (onClick && tag) {
        this.clickHandlers.set(tag, onClick);
      }

      // Handle notification click
      notification.onclick = () => {
        window.focus();
        if (onClick) {
          onClick();
        } else if (tag && this.clickHandlers.has(tag)) {
          this.clickHandlers.get(tag)?.();
        }
        notification.close();
      };

      // Clean up click handler after notification closes
      notification.onclose = () => {
        if (tag) {
          this.clickHandlers.delete(tag);
        }
      };

      // Auto-close after 10 seconds unless requireInteraction is true
      if (!requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 10000);
      }

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  /**
   * Close all notifications with a specific tag
   */
  async closeByTag(tag: string): Promise<void> {
    // This would be handled by service worker in production
    // For now, just remove the click handler
    this.clickHandlers.delete(tag);
  }

  /**
   * Show a notification for a task assignment
   */
  async showTaskNotification(
    title: string,
    message: string,
    taskId: string,
    priority: 'low' | 'normal' | 'high' | 'urgent',
    onNavigate: (url: string) => void
  ): Promise<Notification | null> {
    return this.show({
      title,
      body: message,
      tag: `task-${taskId}`,
      data: { taskId, type: 'task' },
      requireInteraction: priority === 'urgent',
      onClick: () => {
        onNavigate(`/tasks/${taskId}`);
      },
    });
  }

  /**
   * Show a notification for a comment
   */
  async showCommentNotification(
    title: string,
    message: string,
    taskId: string,
    commentId: string,
    onNavigate: (url: string) => void
  ): Promise<Notification | null> {
    return this.show({
      title,
      body: message,
      tag: `comment-${commentId}`,
      data: { taskId, commentId, type: 'comment' },
      onClick: () => {
        onNavigate(`/tasks/${taskId}#comment-${commentId}`);
      },
    });
  }

  /**
   * Show a notification for a meeting transcript
   */
  async showMeetingNotification(
    title: string,
    message: string,
    meetingId: string,
    onNavigate: (url: string) => void
  ): Promise<Notification | null> {
    return this.show({
      title,
      body: message,
      tag: `meeting-${meetingId}`,
      data: { meetingId, type: 'meeting' },
      onClick: () => {
        onNavigate(`/meetings/${meetingId}`);
      },
    });
  }

  /**
   * Show a notification for draft tasks extracted from emails or meetings
   */
  async showDraftTasksNotification(
    title: string,
    message: string,
    sourceId: string,
    sourceType: 'email' | 'meeting',
    onNavigate: (url: string) => void
  ): Promise<Notification | null> {
    return this.show({
      title,
      body: message,
      tag: `draft-tasks-${sourceType}-${sourceId}`,
      data: { sourceId, sourceType, type: 'draft_tasks' },
      onClick: () => {
        onNavigate('/ai-inbox');
      },
    });
  }

  /**
   * Test notification - useful for settings page
   */
  async showTestNotification(): Promise<Notification | null> {
    return this.show({
      title: '🎉 NeoKanban Notifications Enabled',
      body: 'You will now receive browser notifications for important updates.',
      tag: 'test-notification',
      requireInteraction: false,
    });
  }
}

// Export singleton instance
export const browserNotifications = new BrowserNotificationService();

// Export type for use in components
export type { BrowserNotificationService };
